import React, { useState, useEffect, useCallback } from 'react';
import {
  listStaffUsers,
  createStaffUser,
  resetStaffPassword,
  setStaffActive,
  deleteStaffUser,
  checkSuperAdmin,
  STAFF_ROLES,
  type StaffUser,
} from '../../lib/superAdmin';

const ROLE_BADGE: Record<string, { bg: string; text: string; border: string }> = {
  admin: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  executive: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  manager: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  sales_rep: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  accountant: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  marketing: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
};

function roleBadge(role: string) {
  return ROLE_BADGE[role] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };
}

export default function StaffControl() {
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('sales_rep');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);

  // Reset-password modal state
  const [resetTarget, setResetTarget] = useState<StaffUser | null>(null);
  const [resetPw, setResetPw] = useState('');
  const [resetting, setResetting] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<StaffUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const admin = await checkSuperAdmin();
      setIsSuperAdmin(admin);
      if (admin) {
        const list = await listStaffUsers();
        setUsers(list);
      }
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to load staff list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function flashSuccess(msg: string) {
    setActionSuccess(msg);
    setActionError('');
    window.setTimeout(() => setActionSuccess(''), 4000);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setActionError('');
    if (newPassword.length < 10) { setActionError('Password must be at least 10 characters.'); return; }
    setCreating(true);
    try {
      await createStaffUser({ email: newEmail, password: newPassword, name: newName, role: newRole });
      flashSuccess(`Created ${newName} (${newEmail}) — they can log in immediately.`);
      setNewName(''); setNewEmail(''); setNewPassword(''); setNewRole('sales_rep'); setShowCreate(false);
      await refresh();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to create user');
    } finally { setCreating(false); }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    if (!resetTarget) return;
    if (resetPw.length < 10) { setActionError('Password must be at least 10 characters.'); return; }
    setResetting(true);
    try {
      await resetStaffPassword(resetTarget.email, resetPw);
      flashSuccess(`Password reset for ${resetTarget.email} — applies on all their devices instantly.`);
      setResetTarget(null); setResetPw('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally { setResetting(false); }
  }

  async function handleToggle(u: StaffUser) {
    setActionError('');
    try {
      await setStaffActive(u.email, !u.is_active);
      flashSuccess(u.is_active ? `${u.email} suspended — they can no longer sign in.` : `${u.email} reactivated.`);
      await refresh();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to change status');
    }
  }

  async function handleDelete(e: React.FormEvent) {
    e.preventDefault();
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteStaffUser(deleteTarget.email);
      flashSuccess(`${deleteTarget.email} permanently deleted.`);
      setDeleteTarget(null);
      await refresh();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally { setDeleting(false); }
  }

  // ─── Permission gate (server-checked) ───
  if (isSuperAdmin === false) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="h-14 w-14 bg-red-50 text-red-600 rounded-2xl mx-auto flex items-center justify-center text-2xl mb-4 border border-red-100">⛔</div>
        <h3 className="text-sm font-bold text-slate-900">Super Admin Access Required</h3>
        <p className="text-xs text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed">
          Staff Control is restricted to the boss roles (admin / executive). The database verified
          your current session does not carry Super Admin authority.
        </p>
        <p className="text-[11px] text-slate-400 mt-3">Sign in to the main portal with an admin account, then reopen this vault.</p>
      </div>
    );
  }

  if (loading && isSuperAdmin === null) {
    return <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-xs text-slate-400">Verifying Super Admin clearance…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-sm">
        <div>
          <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            👥 Staff Control
            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Super Admin</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Create, reset, suspend or delete staff accounts — changes apply instantly on every device.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-300 font-medium disabled:opacity-50 cursor-pointer"
          >
            {loading ? '⟳ Refreshing…' : '⟳ Refresh'}
          </button>
          <button
            onClick={() => setShowCreate(v => !v)}
            className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm cursor-pointer"
          >
            {showCreate ? '✕ Cancel' : '➕ Create Staff User'}
          </button>
        </div>
      </div>

      {/* Feedback */}
      {actionError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium">⚠️ {actionError}</div>
      )}
      {actionSuccess && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 font-medium">✅ {actionSuccess}</div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white border border-emerald-200 rounded-2xl p-4 space-y-3 shadow-sm">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">New Staff Account</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Full Name</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} required
                placeholder="e.g. Soly Bhalla"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Work Email</label>
              <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required
                placeholder="name@saindustries.com"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Role</label>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none">
                {STAFF_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Starting Password (min 10 chars)</label>
              <input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={10}
                placeholder="Type a strong password — share it privately"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <p className="text-[11px] text-slate-400">The user is created confirmed — no email verification needed. They log in right away.</p>
            <button type="submit" disabled={creating || !newName || !newEmail || !newPassword}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow-sm cursor-pointer">
              {creating ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      )}

      {/* Staff table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 font-semibold text-[11px] uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="py-3 px-4">Staff Member</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-3 text-center">Status</th>
                <th className="py-3 px-3 text-center">Discount</th>
                <th className="py-3 px-3 text-center">Confirmed</th>
                <th className="py-3 px-3">Last Login</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && users.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400">Loading staff directory…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400">No staff found. Create the first one above.</td></tr>
              ) : users.map(u => {
                const badge = roleBadge(u.role);
                return (
                  <tr key={u.email} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center font-bold text-emerald-700 text-xs">
                          {(u.name || u.email).slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{u.name}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${badge.bg} ${badge.text} ${badge.border}`}>
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {u.is_active ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">ACTIVE</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">SUSPENDED</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center font-mono font-bold text-slate-700">{u.discount_limit}%</td>
                    <td className="py-3 px-3 text-center">
                      {u.confirmed ? <span className="text-emerald-600">✓</span> : <span className="text-amber-600" title="Email not confirmed">⚠</span>}
                    </td>
                    <td className="py-3 px-3 text-[11px] text-slate-500 font-mono">
                      {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          onClick={() => { setResetTarget(u); setResetPw(''); setActionError(''); }}
                          className="px-2.5 py-1 text-[11px] bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded border border-blue-200 cursor-pointer"
                        >🔑 Reset PW</button>
                        <button
                          onClick={() => handleToggle(u)}
                          className={`px-2.5 py-1 text-[11px] font-semibold rounded border cursor-pointer ${u.is_active
                            ? 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200'
                            : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'}`}
                        >{u.is_active ? '⏸ Suspend' : '▶ Activate'}</button>
                        <button
                          onClick={() => setDeleteTarget(u)}
                          className="px-2.5 py-1 text-[11px] bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold rounded border border-rose-200 cursor-pointer"
                        >🗑 Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleReset} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-3">
            <h3 className="text-sm font-bold text-slate-900">Reset Password</h3>
            <p className="text-xs text-slate-500">
              For <span className="font-mono text-slate-700">{resetTarget.email}</span>. The new password applies
              instantly on all their devices — their old one stops working immediately.
            </p>
            <input
              autoFocus value={resetPw} onChange={(e) => setResetPw(e.target.value)} required minLength={10}
              placeholder="New password (min 10 characters)"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" onClick={() => setResetTarget(null)}
                className="px-3 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer">Cancel</button>
              <button type="submit" disabled={resetting || resetPw.length < 10}
                className="px-4 py-2 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white cursor-pointer">
                {resetting ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleDelete} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-3">
            <h3 className="text-sm font-bold text-rose-700">⚠️ Permanently delete user?</h3>
            <p className="text-xs text-slate-600">
              This deletes <span className="font-mono font-semibold text-slate-800">{deleteTarget.email}</span> and their
              login forever. This cannot be undone. (Their past quotations stay in the database.)
            </p>
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" onClick={() => setDeleteTarget(null)}
                className="px-3 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer">Keep user</button>
              <button type="submit" disabled={deleting}
                className="px-4 py-2 text-xs font-bold rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white cursor-pointer">
                {deleting ? 'Deleting…' : 'Delete Forever'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
